import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { StaffSetupRequest } from '@/models/payment';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { Box, GridLegacy as Grid } from '@mui/material';
import { InputText } from '@/components/shared/InputText';
import {
    SecondaryButtonStyle,
    PrimaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';

interface StaffConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const StaffConnect = (props: StaffConnectProps) => {
    const { t } = useTranslation();
    const [credentials, setCredentials] = useState<StaffSetupRequest>({
        username: '',
        password: '',
        employeeViewRequestCode: '',
        plandataViewRequestCode: '',
        employeeGroupViewRequestCode: ''
    });
    const { setAlert } = useNotificationsSnackbar();

    const { staffSetup } = usePaymentsMutations();

    const dispatch = useAppDispatch();

    const onClick = () => {
        staffSetup.mutate(credentials);
    };

    useEffect(() => {
        if (staffSetup.isSuccess) {
            props.onSaveSuccess?.();
        }
    }, [staffSetup.isSuccess]);

    useEffect(() => {
        dispatch(setLoader(staffSetup.isPending));
    }, [staffSetup.isPending]);

    useEffect(() => {
        if (staffSetup.isError) {
            setAlert('Connection failed', 'error');
        }
    }, [staffSetup.isError]);

    const isFormValid = () => {
        return (
            credentials.username &&
            credentials.password &&
            credentials.employeeViewRequestCode &&
            credentials.plandataViewRequestCode &&
            credentials.employeeGroupViewRequestCode
        );
    };

    return (
        <>
            <Box width={'100%'} margin={'20px 0 10px'}>
                <InputText
                    value={credentials.username}
                    label={t('username')}
                    placeholder={t('enter_username')}
                    onChange={(e) => {
                        setCredentials({
                            ...credentials,
                            username: e.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    type="password"
                    value={credentials.password}
                    label={t('password')}
                    placeholder={t('enter_password')}
                    onChange={(e) => {
                        setCredentials({
                            ...credentials,
                            password: e.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    value={credentials.employeeViewRequestCode}
                    label={t('employee_view_request_code')}
                    placeholder={t('enter_employee_view_request_code')}
                    onChange={(e) => {
                        setCredentials({
                            ...credentials,
                            employeeViewRequestCode: e.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    value={credentials.plandataViewRequestCode}
                    label={t('plandata_view_request_code')}
                    placeholder={t('enter_plandata_view_request_code')}
                    onChange={(e) => {
                        setCredentials({
                            ...credentials,
                            plandataViewRequestCode: e.target.value
                        });
                    }}
                    spacing="10px 0"
                />

                <InputText
                    value={credentials.employeeGroupViewRequestCode}
                    label={t('employee_group_view_request_code')}
                    placeholder={t('enter_employee_group_view_request_code')}
                    onChange={(e) => {
                        setCredentials({
                            ...credentials,
                            employeeGroupViewRequestCode: e.target.value
                        });
                    }}
                    spacing="10px 0"
                />
            </Box>

            <Grid m={'20px 0'} container justifyContent={'space-around'}>
                <SecondaryButtonStyle
                    onClick={props?.onClose}
                    sx={{ width: '170px' }}
                >
                    {t('cancel')}
                </SecondaryButtonStyle>

                <PrimaryButtonStyle
                    onClick={onClick}
                    sx={{ width: '170px' }}
                    disabled={!isFormValid()}
                    loading={staffSetup.isPending}
                >
                    {t('connect')}
                </PrimaryButtonStyle>
            </Grid>
        </>
    );
};
